import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'

import '@general/ui-kit/theme.css'

import { ensureDemoSession } from '@general/demo/frontend'

import App from './App.vue'
import router from './app/router'
import { moduleMessages, mergeMessages } from './app/registry'
import { SUPPORTED_CODES, STORAGE_KEY, toCode } from './app/languages'
import { setupPwa } from './app/pwa'
import { shareCatalogueWithWorker } from './app/pushCatalogue'
import { setupNativeFeel } from './app/nativeFeel'
import { useTheme, installViewportVars } from '@general/ui-kit'

import es from './i18n/es.json'
import ca from './i18n/ca.json'
import en from './i18n/en.json'
import fr from './i18n/fr.json'

const MESSAGES = { es, ca, en, fr }

// Order of preference: what this person chose, then their browser, then Spanish.
// The company profile overrides this on load if the person never chose.
const stored = localStorage.getItem(STORAGE_KEY)
const locale = stored && SUPPORTED_CODES.includes(stored)
  ? stored
  : toCode(navigator.language)

const i18n = createI18n({
  legacy: false,
  locale,
  // English rather than Spanish: a missing key should surface a term every
  // developer reads, not a half-translated screen.
  fallbackLocale: 'en',
  messages: Object.fromEntries(
    // Modules contribute their own strings, merged branch by branch so a module
    // can name itself inside the platform's own blocks (settings, permissions,
    // audit). The shell wins on a leaf clash: a module fills gaps, it never
    // rewrites platform copy.
    Object.entries(MESSAGES).map(([code, messages]) => [code, mergeMessages(moduleMessages(code), messages)])
  ),
})

document.documentElement.lang = locale

const pinia = createPinia()

// Reset every store when the session ends. On a shared device the next person
// must not see a trace of the previous one. The auth store manages itself.
pinia.use(({ store }) => {
  if (store.$id === 'auth') return
  const initial = JSON.parse(JSON.stringify(store.$state))
  window.addEventListener('general:session-ended', () => {
    try { store.$patch(JSON.parse(JSON.stringify(initial))) } catch { /* not serialisable */ }
  })
})

// ── The demo, if this instance is one ─────────────────────────────────────────
//
// Before the app mounts, and awaited on purpose: `?m=crm,quotes` has to have
// become a session by the time the router makes its first decision, or the
// visitor watches a sign-in screen flash past on the way to a product they are
// being shown. On an ordinary instance the first call answers 404 and this
// costs one request and nothing else.
//
// It never throws: a demo that cannot open still has to render, because the
// screen it renders is the one that says why.
// Where it goes from here is the router's decision, not this file's: the guard
// in app/router.js sends anybody without a session to the selector rather than
// to a sign-in screen a demo instance does not have. Doing it here instead was
// tried and does not work — vue-router reads the address bar when it is built,
// which is before this line runs.
await ensureDemoSession().catch(() => 'none')

const app = createApp(App)

// Without this, a render error is a blank page and no clue as to why.
app.config.errorHandler = (error, _instance, info) => {
  console.error('[vue]', info, error)
}

app.use(pinia)
app.use(router)
app.use(i18n)
app.mount('#app')

// The theme follows the device live on every screen, the login included, and
// not only inside the shells that draw the switcher.
useTheme()


// What the browser still does that an app never does: pinch zoom on iOS, and
// in the installed app, browser zoom and the long-press menu. Listeners on the
// window and the document, so it does not matter that the app mounted first.
setupNativeFeel()

// El teclado del teléfono, para toda la app: el shell y cada hoja miden lo que
// queda por encima de él (--app-height, --keyboard-inset, html.keyboard-open).
installViewportVars()

// Installability and update detection. After mount, so a service worker
// registration can never delay the first paint.
setupPwa()

// The words a push needs to arrive in the person's language: the service
// worker has no catalogue of its own (app/pushCatalogue.js).
shareCatalogueWithWorker(i18n, { build: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev' })
